import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Loader, AlertCircle, Compass, Scale, Users, BookOpen, CalendarClock,
} from 'lucide-react';
import { fetchProceedingDigest, requestProceedingDigest, ProceedingDigest, ExpressSentiment } from '../lib/digestService';
import { FollowButton } from '../components/FollowButton';
import { track } from '../utils/analytics';
import { uploadFilingsParallel, type MinimalFiling } from '../lib/parallelFilingUpload';
import { useDocketMetadata } from '../hooks/useDocketMetadata';
import { formatDeadlineCalendarDate } from '../utils/nprmHelpers';

// Brand-led, restrained categorical palette (green first) — replaces the generic
// primary-color rainbow. Issues are sorted by filer count, so the top issue + the
// largest topic-map node lead with the CommsGenie green, not blue.
const ISSUE_COLORS = ['#2e9e65', '#0f766e', '#b45309', '#7c3aed', '#475569', '#0891b2', '#9d174d'];
const STANCE_COLORS: Record<string, string> = {
  support: '#059669', oppose: '#dc2626', modify: '#d97706', mixed: '#7c3aed', other: '#64748b',
};

/** Load vis-network once from CDN (kept out of package.json deps). */
function useVisNetwork() {
  const [ready, setReady] = useState<boolean>(() => !!(window as any).vis);
  useEffect(() => {
    if ((window as any).vis) { setReady(true); return; }
    const existing = document.getElementById('vis-network-cdn');
    if (existing) { existing.addEventListener('load', () => setReady(true)); return; }
    const s = document.createElement('script');
    s.id = 'vis-network-cdn';
    s.src = 'https://unpkg.com/vis-network/standalone/umd/vis-network.min.js';
    s.async = true;
    s.onload = () => setReady(true);
    document.body.appendChild(s);
  }, []);
  return ready;
}

function TopicMap({ digest }: { digest: ProceedingDigest }) {
  const ref = useRef<HTMLDivElement>(null);
  const visReady = useVisNetwork();
  useEffect(() => {
    const vis = (window as any).vis;
    if (!visReady || !vis || !ref.current || !digest.graph?.nodes?.length) return;
    const network = new vis.Network(
      ref.current,
      { nodes: new vis.DataSet(digest.graph.nodes), edges: new vis.DataSet(digest.graph.edges) },
      {
        nodes: { shape: 'dot', scaling: { min: 6, max: 48, label: { min: 12, max: 21, drawThreshold: 6 } } },
        edges: { width: 0.5, smooth: { type: 'continuous' }, color: { opacity: 0.3 } },
        groups: {
          issue: { font: { size: 18, color: '#0f172a', strokeWidth: 4, strokeColor: '#fff' }, borderWidth: 2 },
          filer: { color: { background: '#cbd5e1', border: '#94a3b8' }, font: { size: 10, color: '#475569' } },
        },
        physics: {
          // stabilize, then freeze — large dockets (25-306 ≈ 100 nodes / 400 edges)
          // never settle under a live force sim, so the graph jitters forever.
          // Strong repulsion + long springs + max avoidOverlap so the nodes fan
          // out across the canvas on first load instead of clumping in the center.
          barnesHut: { gravitationalConstant: -14000, centralGravity: 0.15, springLength: 230, springConstant: 0.02, damping: 0.4, avoidOverlap: 1 },
          stabilization: { iterations: 600, fit: true },
        },
        interaction: { hover: true, tooltipDelay: 120, dragNodes: true },
      }
    );
    // Once the initial layout settles, turn physics OFF so nodes stay put (still
    // draggable). Without this, big graphs oscillate indefinitely.
    network.once('stabilizationIterationsDone', () => {
      network.setOptions({ physics: false });
    });
    return () => network.destroy();
  }, [visReady, digest]);

  return (
    <div className="relative">
      <div ref={ref} className="h-[460px] w-full rounded-xl border border-cg-border bg-cg-cream" />
      {!visReady && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
          <Loader className="w-4 h-4 mr-2 animate-spin" /> loading topic map…
        </div>
      )}
    </div>
  );
}

/** Public express-comment sentiment — the mass-comment ("what the public said")
 *  layer, distinct from the industry issue analysis. Shown for viral/mass-comment
 *  dockets; for express-only dockets it's the whole digest. */
/** The one-line, plain-language takeaway, derived from the actual split. */
function expressBottomLine(pct: Record<string, number>): string {
  const s = pct.support || 0, o = pct.oppose || 0;
  const favor = s >= o;
  const dir = favor ? 'in favor' : 'opposed';
  const diff = Math.abs(s - o);
  if (diff <= 8) return `Public comments are split roughly evenly${diff <= 2 ? '' : `, leaning ${dir}`}`;
  const hi = Math.max(s, o), lo = Math.min(s, o);
  if (lo <= 5) return `Public comments run overwhelmingly ${dir}`;
  const r = hi / lo;
  if (r >= 1.75) return `Public comments run about ${Math.round(r)}-to-1 ${dir}`;
  return `Public comments lean ${dir}`;
}

const STANCE_LABEL: Record<string, string> = { support: 'Support', oppose: 'Oppose', other: 'Unclear' };

function ExpressSentimentSection({ es }: { es: ExpressSentiment }) {
  const pct = es.sentiment_pct || {};
  const order = ['support', 'oppose', 'other'] as const;
  const fmt = (n: number) => (n ?? 0).toLocaleString();
  const velMax = Math.max(1, ...(es.velocity || []).map((v) => v.n));
  const directPct = es.direct_pct ?? es.coverage_pct;   // new vs legacy
  const sampleN = es.n_classified ?? es.n_analyzed;
  const moe = sampleN ? Math.max(1, Math.round(98 / Math.sqrt(sampleN))) : null;

  return (
    <>
      <h2 className="text-xs uppercase tracking-wider text-slate-500 mt-9 mb-1 pb-1.5 border-b border-slate-200 flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Public comments — {fmt(es.n_express)} filings
      </h2>
      <p className="text-sm text-slate-500 mb-3">
        Sentiment of the general public's web-form comments — <b>separate from the industry
        positions above</b>. {es.n_express.toLocaleString()} comments, {es.n_unique_variants.toLocaleString()} distinct
        versions after grouping form letters.
      </p>

      {/* BOTTOM LINE — the takeaway, before the numbers */}
      <div className="rounded-r-lg bg-cg-cream px-4 py-3 mb-3" style={{ borderLeft: '4px solid #0f766e' }}>
        <div className="font-semibold text-slate-900">Bottom line: {expressBottomLine(pct)}.</div>
        <div className="text-sm text-slate-600 mt-1">
          This reflects the volume of public write-in comments — not the weight of legal argument,
          or the standard the FCC applies in deciding the proceeding.
        </div>
      </div>

      {/* split bar */}
      <div className="flex h-6 w-full overflow-hidden rounded-full border border-cg-border">
        {order.map((k) => (pct[k] ? (
          <div key={k} style={{ width: `${pct[k]}%`, background: STANCE_COLORS[k] }} className="h-full"
            title={`${STANCE_LABEL[k]} ${pct[k]}%`} />
        ) : null))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
        {order.map((k) => (pct[k] ? (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: STANCE_COLORS[k] }} />
            <b>{pct[k]}%</b> {STANCE_LABEL[k].toLowerCase()} <span className="text-slate-400">({fmt(es.sentiment[k])})</span>
          </span>
        ) : null))}
        <span className="text-slate-400">estimated</span>
      </div>

      {/* inline legend: what the stances mean */}
      <p className="text-xs text-slate-500 mt-2">
        <b>Support</b> = wants the FCC to adopt/keep the action · <b>Oppose</b> = wants it rejected ·
        <b> Unclear</b> = neutral or off-topic.
      </p>

      {/* how we know — the estimate framed as a strength, not a footnote */}
      {directPct != null && (
        <p className="text-xs text-slate-500 mt-1.5">
          <b>How we know:</b> {directPct}% of comments are grouped form letters we classified exactly;
          the rest are estimated from a random sample of {fmt(sampleN || 0)} distinct comments
          {moe ? <> — accurate to about ±{moe} points</> : null}.
        </p>
      )}

      {/* comment volume by week — the surge is the story */}
      {es.velocity && es.velocity.length > 1 && (
        <>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mt-6 mb-2">Comment volume by week</h3>
          <div className="flex items-end gap-1 h-16">
            {es.velocity.map((v, i) => (
              <div key={i} className="flex-1 bg-cg-green/70 rounded-t min-h-[2px]"
                style={{ height: `${Math.max(2, (v.n / velMax) * 100)}%` }}
                title={`Week of ${v.week}: ${fmt(v.n)}`} />
            ))}
          </div>
        </>
      )}

      {/* top themes */}
      {es.themes && es.themes.length > 0 && (
        <>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mt-6 mb-2">Top themes</h3>
          <div className="flex flex-wrap gap-1.5">
            {es.themes.map((t, i) => {
              const c = STANCE_COLORS[t.stance] || STANCE_COLORS.other;
              return (
                <span key={i} className="text-[13px] rounded-full px-2.5 py-0.5 border"
                  style={{ borderColor: `${c}55`, color: c }}>
                  {t.label} <span className="text-slate-400">· {fmt(t.n)}</span>
                </span>
              );
            })}
          </div>
        </>
      )}

      {/* representative language (balanced across stances by the backend) */}
      {es.representative && es.representative.length > 0 && (
        <>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mt-6 mb-2">What people wrote</h3>
          <ul className="list-none p-0 space-y-2.5">
            {es.representative.map((r, i) => (
              <li key={i} className="text-[15px] border-l-2 pl-3"
                style={{ borderColor: STANCE_COLORS[r.stance] || STANCE_COLORS.other }}>
                <span className="text-[11px] font-semibold uppercase tracking-wide mr-1.5"
                  style={{ color: STANCE_COLORS[r.stance] || STANCE_COLORS.other }}>
                  {STANCE_LABEL[r.stance] || 'Unclear'}
                </span>
                <span className="text-slate-700">“{r.excerpt}”</span>
                {r.count > 1 && (
                  <span className="text-slate-400 text-xs ml-1 whitespace-nowrap">— {fmt(r.count)} filed this</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* methodology — collapsed by default, but present in the DOM for SEO/trust */}
      <details className="mt-4 text-sm">
        <summary className="cursor-pointer text-cg-green font-medium">How this sentiment is measured</summary>
        <p className="text-slate-500 mt-2">
          We read the public express (web-form) comments from the FCC's ECFS record. Identical
          form-letter submissions are grouped and counted exactly. Because most comments here are
          individually written, we also classify a random sample of the distinct versions and
          extrapolate their support/oppose/unclear mix to the full set — so the split reflects all
          {' '}{es.n_express.toLocaleString()} comments, not just the most-copied ones. This is public
          sentiment only; it is not a measure of the legal merits or of how the Commission will rule.
        </p>
      </details>
    </>
  );
}

/** Prerender/SSG handoff: scripts/prerender.js bakes the built digest into the
 *  page as window.__DIGEST__, so this route paints full, indexable content on the
 *  very first render — no client fetch needed for crawlers, and instant load for
 *  users. Guarded by a docket match so SPA navigation to a different proceeding
 *  never shows the initially-loaded docket's stale data. */
function preloadedDigest(docket?: string): ProceedingDigest | null {
  if (typeof window === 'undefined') return null;
  const pre = (window as any).__DIGEST__ as ProceedingDigest | undefined;
  return pre && docket && pre.docket === docket ? pre : null;
}

export function ProceedingDigestPage() {
  const { docketNumber } = useParams<{ docketNumber: string }>();
  // Filing period + deadlines (incl. extensions) — same source the proceeding page uses.
  const {
    commentDeadline, replyCommentDeadline, commentDeadlineExtension, replyDeadlineExtension,
  } = useDocketMetadata(docketNumber);
  const preloaded = preloadedDigest(docketNumber);
  const [digest, setDigest] = useState<ProceedingDigest | null>(preloaded);
  const [error, setError] = useState<string | null>(null);
  // phase: loading -> present | absent (no digest yet) | building (requested) | error
  const [phase, setPhase] = useState<'loading' | 'present' | 'absent' | 'building' | 'error'>(
    preloaded ? 'present' : 'loading',
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [buildMsg, setBuildMsg] = useState<string>('Analyzing the proceeding…');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let alive = true;
    // If prerender handed us this docket's digest, keep showing it while we
    // refresh in the background — don't flash a spinner, and don't downgrade to
    // 'absent'/'error' on a transient miss.
    const pre = preloadedDigest(docketNumber);
    if (pre) { setDigest(pre); setPhase('present'); }
    else { setPhase('loading'); }
    setError(null); setNotice(null);
    fetchProceedingDigest(docketNumber || '')
      .then((d) => {
        if (!alive) return;
        if (d) { setDigest(d); setPhase('present'); }
        else if (!pre) { setPhase('absent'); }
      })
      .catch((e) => { if (alive && !pre) { setError(e?.message || 'Failed to load digest'); setPhase('error'); } });
    return () => { alive = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, [docketNumber]);

  // Funnel top: fire once when a real digest renders for this docket.
  useEffect(() => {
    if (phase === 'present' && digest?.status === 'ok') {
      track('digest_view', {
        docket_number: docketNumber,
        express_only: !!digest.express_only,
        has_express: !!digest.express_sentiment,
        n_issues: digest.issues?.length ?? 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, docketNumber]);

  // Ingest a docket's comment PDFs into Storage using the EXISTING pipeline
  // (enroll-docket + uploadFilingsParallel) — same path the proceeding page uses.
  // The digest compiler reads PDFs straight from Storage, so we only need the
  // uploads to finish (not the text-extraction worker).
  const ingestComments = async (): Promise<boolean> => {
    try {
      const enrollUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/enroll-docket`;
      const res = await fetch(enrollUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ docketNumber }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      const filings: MinimalFiling[] = (Array.isArray(data.filings) ? data.filings : []).filter(
        (f: any) => !(f.express_comment === 1 || f.express_comment === true)
      );
      if (filings.length === 0) return false;
      setBuildMsg(`Loading ${filings.length} filings…`);
      await uploadFilingsParallel(filings, docketNumber!, (c, t) =>
        setBuildMsg(`Loading filings… ${c}/${t}`)
      );
      return true;
    } catch {
      return false;
    }
  };

  const handleGenerate = async () => {
    if (!docketNumber) return;
    setNotice(null); setBuildMsg('Analyzing the proceeding…'); setPhase('building');
    let res;
    try {
      res = await requestProceedingDigest(docketNumber);
      // Fresh, un-ingested small docket: run the existing ingest, then re-request.
      if (res.state === 'needs_ingest') {
        setBuildMsg('Loading the proceeding’s filings…');
        await ingestComments();
        setBuildMsg('Analyzing the comments…');
        res = await requestProceedingDigest(docketNumber);
      }
    } catch (e: any) {
      setNotice(e?.message || 'Could not start the digest.'); setPhase('absent');
      return;
    }
    if (res.state === 'no_comments') {
      setNotice('No comments have been filed in this proceeding yet.'); setPhase('absent'); return;
    }
    if (res.state === 'needs_ingest') {
      setNotice('Couldn’t load the filings for this proceeding — please try again shortly.'); setPhase('absent'); return;
    }
    if (res.state === 'queued') {
      setNotice(
        `This proceeding has ${res.count ?? 'many'} filings — too many to analyze on the spot. ` +
        'We’ve noted your interest and will process it; the digest will appear here.'
      );
      setPhase('absent'); return;
    }
    // poll until the digest is ready. A fresh-docket 'analyzing' run (ingest +
    // extraction) takes longer than a cached rebuild, so give it a wider window.
    const maxTries = res.state === 'analyzing' ? 100 : 40; // ~8 min vs ~3 min
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const d = await fetchProceedingDigest(docketNumber);
        if (d) {
          if (pollRef.current) clearInterval(pollRef.current);
          setDigest(d); setPhase('present');
        } else if (tries >= maxTries) {
          if (pollRef.current) clearInterval(pollRef.current);
          setNotice('This is taking longer than expected — check back shortly.'); setPhase('absent');
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const orderedIssues = useMemo(
    () => (digest?.issues ? [...digest.issues].sort((a, b) => b.n_filers - a.n_filers) : []),
    [digest]
  );

  // Which filing period are we in right now, from the deadlines.
  const period = useMemo(() => {
    const now = new Date();
    if (commentDeadline && now < commentDeadline) return 'Comment period — open';
    if (replyCommentDeadline && now < replyCommentDeadline) return 'Reply-comment period — open';
    if (commentDeadline || replyCommentDeadline) return 'Comment period — closed';
    return null;
  }, [commentDeadline, replyCommentDeadline]);

  const extNote = (ext: typeof commentDeadlineExtension) =>
    ext?.from_date
      ? ` (extended from ${formatDeadlineCalendarDate(new Date(`${ext.from_date}T00:00:00Z`).toISOString())})`
      : '';

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#13241c] to-[#1c3a2b] text-white">
        <div className="max-w-4xl mx-auto px-5 py-8">
          <Link to={`/proceedings/${docketNumber}`}
            className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to proceeding
          </Link>
          <div className="text-emerald-300 text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5" /> Proceeding digest
          </div>
          <h1 className="text-2xl md:text-3xl font-display mt-1">{docketNumber} — Comment Digest</h1>

          {(period || commentDeadline || replyCommentDeadline) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {period && (
                <span className="inline-flex items-center gap-1.5 font-medium text-emerald-200">
                  <CalendarClock className="w-4 h-4" /> {period}
                </span>
              )}
              {commentDeadline && (
                <span className="text-slate-300">
                  Comments due <b className="text-white">{formatDeadlineCalendarDate(commentDeadline.toISOString())}</b>
                  {extNote(commentDeadlineExtension)}
                </span>
              )}
              {replyCommentDeadline && (
                <span className="text-slate-300">
                  Replies due <b className="text-white">{formatDeadlineCalendarDate(replyCommentDeadline.toISOString())}</b>
                  {extNote(replyDeadlineExtension)}
                </span>
              )}
            </div>
          )}

          {digest && digest.status === 'ok' && orderedIssues.length > 0 && (
            <div className="text-slate-300 text-sm mt-1">
              <b className="text-white">{digest.n_filers}</b> filers · <b className="text-white">{digest.n_positions}</b> positions
              · {orderedIssues.length} key issues
            </div>
          )}
          {digest && digest.status === 'ok' && orderedIssues.length === 0 && digest.express_sentiment && (
            <div className="text-slate-300 text-sm mt-1">
              <b className="text-white">{digest.express_sentiment.n_express.toLocaleString()}</b> public comments
            </div>
          )}
          {digest && digest.built_at && (
            <div className="text-slate-400 text-xs mt-1.5">Updated {digest.built_at}</div>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5">
        {/* Orientation note — plain text, not a badge */}
        <p className="my-5 text-sm text-slate-500">
          Orientation summary — a fast, cited starting point, not a substitute for reading the filings.
          Generated from the public ECFS record; verify positions against the source before relying on them.
        </p>

        {/* Two-axis legend: the distinction the reader must hold in mind. Only shown
            when both layers are present (otherwise there's nothing to distinguish). */}
        {digest?.status === 'ok' && orderedIssues.length > 0 && digest.express_sentiment && (
          <div className="text-xs text-slate-600 border border-cg-border rounded-lg px-3 py-2 mb-4">
            <span className="inline-flex items-center gap-1.5 mr-3">
              <Scale className="w-3.5 h-3.5 text-slate-400" /> <b>Key issues</b> — substantive positions from named filers
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-slate-400" /> <b>Public comments</b> — general-public sentiment, analyzed separately
            </span>
          </div>
        )}

        {phase === 'loading' && (
          <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
            <Loader className="w-5 h-5 animate-spin" /> Loading…
          </div>
        )}
        {phase === 'building' && (
          <div className="flex flex-col items-center gap-2 text-slate-500 py-16 text-center">
            <Loader className="w-6 h-6 animate-spin text-cg-green" />
            <div className="font-medium text-slate-700">{buildMsg}</div>
            <div className="text-sm">Loading filings, clustering the issues, and synthesizing positions. For a new docket this can take a few minutes.</div>
          </div>
        )}
        {phase === 'absent' && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Compass className="w-8 h-8 text-slate-300" />
            <div className="text-slate-600">No digest has been generated for this proceeding yet.</div>
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-1.5 rounded-lg bg-cg-green px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#268a57]"
            >
              Generate digest <ArrowRight className="w-4 h-4" />
            </button>
            {notice && <div className="text-sm text-amber-700 max-w-md">{notice}</div>}
          </div>
        )}
        {phase === 'error' && (
          <div className="flex items-center gap-2 text-red-600 py-16 justify-center">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}
        {phase === 'present' && digest?.status === 'insufficient_data' && (
          <div className="flex flex-col items-center gap-2 text-slate-500 py-16 text-center">
            <Compass className="w-8 h-8 text-slate-300" />
            <div className="max-w-md">
              {digest.reason === 'no_analyzable_comments'
                ? 'We analyzed this proceeding’s filings but couldn’t extract enough substantive positions to build a digest. This usually means the comments are short form letters or off-topic submissions.'
                : 'Not enough distinct commenter positions yet for a digest on this proceeding.'}
            </div>
          </div>
        )}

        {phase === 'present' && digest?.status === 'ok' && (
          <>
            {orderedIssues.length > 0 && (
              <>
            {/* Topic map */}
            <h2 className="text-xs uppercase tracking-wider text-slate-500 mt-8 mb-3 pb-1.5 border-b border-slate-200">
              Topic map — what the docket is fighting about
            </h2>
            <TopicMap digest={digest} />
            <div className="text-xs text-slate-500 mt-2">
              ● bigger nodes = more filers engaged · drag to explore · each line is a filer's position on an issue.
            </div>

            {/* Key issues */}
            <h2 className="text-xs uppercase tracking-wider text-slate-500 mt-9 mb-2 pb-1.5 border-b border-slate-200">
              Key issues (auto-discovered)
            </h2>
            <p className="text-sm text-slate-500 mb-3">
              Auto-discovered by clustering {digest.n_positions} commenter positions — what the
              industry actually engaged on, not the NPRM's question list.
            </p>
            {orderedIssues.map((iss, ti) => {
              const color = ISSUE_COLORS[ti % ISSUE_COLORS.length];
              return (
                <section key={iss.title + ti} className="my-3.5 rounded-r-xl bg-cg-cream px-4 py-3.5"
                  style={{ borderLeft: `4px solid ${color}` }}>
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                    <h3 className="text-lg font-semibold flex-1 m-0">
                      {iss.id ? (
                        <Link to={`/proceedings/${docketNumber}/digest/${iss.id}`}
                          className="text-slate-900 no-underline hover:text-cg-green hover:underline">
                          {iss.title}
                        </Link>
                      ) : iss.title}
                    </h3>
                    <span className="text-xs font-semibold text-slate-600 bg-slate-200 rounded-full px-2.5 py-0.5">
                      {iss.n_filers} filers
                    </span>
                  </div>
                  {iss.desc && <p className="text-sm text-slate-500 mt-1.5 mb-2">{iss.desc}</p>}
                  {iss.proposition && (
                    <p className="text-xs text-slate-600 mt-1 mb-1.5">
                      <span className="font-semibold text-slate-500">Filers split on: </span>
                      “{iss.proposition}”
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {(['support', 'oppose', 'modify', 'mixed'] as const).map((b) =>
                      iss.stance?.[b] ? (
                        <span key={b} className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                          style={{ background: `${STANCE_COLORS[b]}1a`, color: STANCE_COLORS[b] }}>
                          {iss.stance[b]} {b}
                        </span>
                      ) : null
                    )}
                  </div>
                  <ul className="mt-2 list-none p-0">
                    {iss.positions.map((p, pi) => (
                      <li key={pi} className="py-1.5 border-t border-dashed border-slate-200 text-[15px]">
                        <span className="font-semibold">{p.filer}</span>
                        {p.stance && p.stance !== 'other' ? (
                          <span
                            className="ml-1.5 align-middle text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{
                              background: `${STANCE_COLORS[p.stance] || STANCE_COLORS.other}1a`,
                              color: STANCE_COLORS[p.stance] || STANCE_COLORS.other,
                            }}
                          >
                            {p.stance}
                          </span>
                        ) : null}
                        <span className="text-slate-500"> — </span>
                        {p.summary}
                        {(() => {
                          const pages = p.pages?.length ? p.pages : p.page != null ? [p.page] : [];
                          return pages.length ? (
                            <span className="text-cg-green text-xs font-semibold ml-1 whitespace-nowrap">
                              {pages.length > 1 ? 'pp.' : 'p.'} {pages.join(', ')}
                            </span>
                          ) : null;
                        })()}
                      </li>
                    ))}
                  </ul>
                  {iss.id && iss.n_filers > iss.positions.length && (
                    <Link to={`/proceedings/${docketNumber}/digest/${iss.id}`}
                      className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-cg-green no-underline hover:underline">
                      See what all {iss.n_filers} filers said <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </section>
              );
            })}

            {/* Authorities */}
            {digest.authorities?.length > 0 && (
              <>
                <h2 className="text-xs uppercase tracking-wider text-slate-500 mt-9 mb-3 pb-1.5 border-b border-slate-200 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" /> Most-cited authorities
                </h2>
                <ul className="list-none p-0">
                  {digest.authorities.map((a, i) => (
                    <li key={i} className="py-1.5 border-t border-dashed border-slate-200 text-sm">
                      <span className="font-semibold">{a.citation}</span>
                      <span className="text-slate-500"> — cited by {a.n_filers} filer(s)</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Filers — collapsed by default (long, low scan value) but kept in the
                DOM so filer names stay indexable and searchable. */}
            <details className="mt-9">
              <summary className="text-xs uppercase tracking-wider text-slate-500 mb-3 pb-1.5 border-b border-slate-200 flex items-center gap-1.5 cursor-pointer list-none">
                <Users className="w-3.5 h-3.5" /> Who filed ({digest.filers.length})
                <span className="text-cg-green normal-case tracking-normal font-medium ml-1">show all</span>
              </summary>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {digest.filers.map((f, i) => (
                  <span key={i} className="bg-cg-cream border border-cg-border rounded-full px-2.5 py-0.5 text-[13px] text-slate-700">
                    {f}
                  </span>
                ))}
              </div>
            </details>
              </>
            )}

            {/* Public express-comment sentiment (mass-comment / viral dockets) */}
            {digest.express_sentiment && <ExpressSentimentSection es={digest.express_sentiment} />}

            {/* CTAs */}
            <h2 className="text-xs uppercase tracking-wider text-slate-500 mt-9 mb-3 pb-1.5 border-b border-slate-200 flex items-center gap-1.5">
              <Scale className="w-3.5 h-3.5" /> Go deeper
            </h2>
            <div className="flex flex-wrap gap-3 mb-12">
              {/* Track — real follow action (handles sign-in intent + watchlist limit). */}
              <div className="flex-1 min-w-[240px] rounded-xl p-4 bg-white border border-slate-200">
                <b className="block text-[15px]">Track this proceeding</b>
                <span className="text-[13px] text-slate-500 block mb-3">
                  Get alerted — with the summary — the moment new comments or orders land.
                </span>
                {docketNumber && <FollowButton docketNumber={docketNumber} source="digest" />}
              </div>
              {/* Summarize — the personalized, position-scoped brief. Deep-link straight
                  to the summaries tab (not the Overview tab). */}
              <Link to={`/proceedings/${docketNumber}?tab=comment_summaries`}
                onClick={() => track('digest_summarize_click', { docket_number: docketNumber })}
                className="flex-1 min-w-[240px] rounded-xl p-4 bg-cg-green text-white no-underline">
                <b className="block text-[15px]">Summarize this for your position →</b>
                <span className="text-[13px] text-white/80">Pick your topics &amp; client angle; get a tailored, cited brief.</span>
              </Link>
              {/* Substantiate — the one job: your position, grounded in the record. */}
              <Link to={`/proceedings/${docketNumber}/reply`}
                onClick={() => track('digest_draft_click', { docket_number: docketNumber })}
                className="flex-1 min-w-[240px] rounded-xl p-4 bg-white border border-slate-200 no-underline text-inherit hover:border-cg-green">
                <b className="block text-[15px] flex items-center gap-1.5">Substantiate your position →
                  <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 border border-amber-200">Beta</span>
                </b>
                <span className="text-[13px] text-slate-500">Give a few bullets; we substantiate them against what others filed. Prosumer &amp; Pro.</span>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProceedingDigestPage;
